import { createRootRoute, Outlet, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useAppStore } from '~/lib/store'
import { Search } from '~/components/Search'

export const Route = createRootRoute({
    component: RootLayout,
})

function RootLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const { user, isAuthenticated, isLoading, checkAuth, login, logout } = useAppStore()

    useEffect(() => {
        checkAuth()
    }, [checkAuth])

    return (
        <div className="h-screen bg-slate-900 text-slate-100 flex overflow-hidden">
            {/* Sidebar */}
            <aside
                className={`${sidebarOpen ? 'w-64' : 'w-16'
                    } bg-slate-800 border-r border-slate-700 flex flex-col transition-all duration-300`}
            >
                {/* Logo */}
                <div className="h-14 flex items-center px-4 border-b border-slate-700">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="text-2xl">📚</span>
                        {sidebarOpen && (
                            <span className="font-semibold text-white">Legali Docs</span>
                        )}
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-3 space-y-1">
                    <NavItem to="/" icon="🏠" label="Dashboard" collapsed={!sidebarOpen} />
                    <NavItem to="/docs" icon="📖" label="Documentation" collapsed={!sidebarOpen} />
                    <NavItem to="/docs/mcp" icon="🧩" label="MCP" collapsed={!sidebarOpen} />
                    <NavItem to="/store" icon="🗄️" label="Stored Docs" collapsed={!sidebarOpen} />
                    <NavItem to="/tasks" icon="✅" label="Task Board" collapsed={!sidebarOpen} />
                </nav>

                {/* Collapse button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-3 border-t border-slate-700 hover:bg-slate-700/50 transition-colors"
                >
                    <span className={`block transition-transform ${sidebarOpen ? '' : 'rotate-180'}`}>
                        ◀
                    </span>
                </button>
            </aside>

            {/* Main content */}
            <main className="flex-1 flex flex-col overflow-hidden">
                {/* Top bar */}
                <header className="h-14 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <Search />
                    </div>
                    <div className="flex items-center gap-4">
                        {isLoading ? (
                            <span className="text-sm text-slate-400">Loading...</span>
                        ) : isAuthenticated && user ? (
                            <div className="flex items-center gap-3">
                                <img
                                    src={user.avatar_url}
                                    alt={user.login}
                                    className="w-8 h-8 rounded-full"
                                />
                                <span className="text-sm">{user.login}</span>
                                <button
                                    onClick={logout}
                                    className="text-sm text-slate-400 hover:text-white"
                                >
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={login}
                                className="flex items-center gap-2 px-4 py-2 bg-white text-slate-900 rounded-lg font-medium hover:bg-slate-100 transition-colors text-sm"
                            >
                                <GithubIcon />
                                Sign in
                            </button>
                        )}
                    </div>
                </header>

                {/* Page content */}
                <div className="flex-1 overflow-hidden">
                    <Outlet />
                </div>
            </main>
        </div>
    )
}

function NavItem({ to, icon, label, collapsed }: {
    to: string
    icon: string
    label: string
    collapsed: boolean
}) {
    return (
        <Link
            to={to}
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors [&.active]:bg-blue-600/20 [&.active]:text-blue-400"
        >
            <span className="text-lg">{icon}</span>
            {!collapsed && <span className="text-sm">{label}</span>}
        </Link>
    )
}

function GithubIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
    )
}
